import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Div100vh from "react-div-100vh";
import { format } from "date-fns";
import Header from "@/b2b/components/Header";
import { fetchData, getDataB2B } from "@/lib/helper";
import { Pointer } from "lucide-react";
import PrimaryButton from "../components/PrimaryButton";
import { partnerNavigationRules } from "@/b2b/config/partnerJourney";


const TENURE_PARTNER = "housr";

// The tenure the resident is told about is the LEASE (loanTenure + 1, the same derivation
// used everywhere else in the codebase) — 6 months. The rent itself is financed over the
// loan tenure, 5 months. Both numbers are shown, because they are genuinely different and
// a resident who spots the gap later should not feel it was hidden.
const DEFAULT_LOAN_TENURE_MONTHS = 5;

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : format(date, "d MMM yyyy");
};

const TenureScreen = ({ theme }) => {
  const navigate = useNavigate();
  const { userIdentifier } = useParams();
  const [journey, setJourney] = useState(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("user_identifier");
  const partner = localStorage.getItem("partnerIdentifier");

  const loanTenure =
    journey?.loanTenure || lead?.tenure || DEFAULT_LOAN_TENURE_MONTHS;
  const leaseMonths = loanTenure + 1;

  const lockInPeriod = lead?.lockInPeriod ?? journey?.lockInPeriod;
  const lockInStart = formatDate(lead?.movein_date ?? journey?.moveInDate);
  const lockInEnd = formatDate(lead?.lockInEndDate ?? journey?.lockInEndDate);

  const notes = [
    `Most of our residents stay on and renew, so we've moved to a single ${leaseMonths}-month lease tenure. That means one set of paperwork instead of repeating this every few months.`,
    lockInPeriod && lockInEnd
      ? `Your lock-in is only ${lockInPeriod} ${
          lockInPeriod === 1 ? "month" : "months"
        } and ends on ${lockInEnd}. After that you're free to leave — but you don't have to.`
      : `Your lock-in is the period you selected on the previous screen. After it ends you're free to leave — but you don't have to.`,
    `Planning to move out? Please give us 1 month's notice post your lock-in ends.`,
    `Your rent is auto-debited every month across the ${loanTenure} months it's financed over.`,
  ];

  const handleNext = () => {
    if (loading) return;
    setLoading(true);

    // Same routing decision the rent summary makes, so this screen slots in without
    // changing where the journey goes next.
    const nextStep =
      journey?.maintenanceAmount && partner !== "kots"
        ? "maintenance-details"
        : partnerNavigationRules?.[partner]?.rentSummary || "onboarding";

    navigate(`/b/${userIdentifier || token}?step=${nextStep}&partner=${partner}`);
  };

  // Read the LEAD as well as the journey. The resident's lock-in is written to the lead
  // synchronously when they pick it; the copy on the journey arrives via a fire-and-forget
  // sync that can fail silently, so the journey alone is not a reliable source here.
  const loadData = async () => {
    const [journeyRes, leadRes] = await Promise.allSettled([
      fetchData("journey/full"),
      getDataB2B("b2b/onboard/start", token),
    ]);

    if (journeyRes.status === "fulfilled") setJourney(journeyRes.value?.journey);
    else console.error(journeyRes.reason);

    if (leadRes.status === "fulfilled") setLead(leadRes.value?.user);
    else console.error(leadRes.reason);
  };

  useEffect(() => {
    loadData();
  }, [token]);

  // A non-Housr resident landing here (back button, stale URL, a partner added to the
  // route later) must not see Housr's tenure copy — send them on instead of rendering it.
  useEffect(() => {
    if (partner && partner !== TENURE_PARTNER) {
      navigate(`/b/${userIdentifier || token}?step=onboarding&partner=${partner}`, {
        replace: true,
      });
    }
  }, [partner, token, userIdentifier]);

  if (partner !== TENURE_PARTNER) return null;

  return (
    <Div100vh
      className={`flex flex-col justify-between p-8 gap-6 ${theme?.backgroundColor} ${theme?.text}`}
    >
      <div className="space-y-6">
        <Header
          heading={`Your lease tenure — ${leaseMonths} months`}
          subheading={`Why ${leaseMonths} months? 🤔`}
        />

        <div className={`${theme?.subHeadingColor} text-wrap`}>
          Your rent is financed over{" "}
          <span className="font-semibold">{loanTenure} months</span>, and your
          lease tenure is{" "}
          <span className="font-semibold">{leaseMonths} months</span>.
        </div>

        {lockInPeriod && lockInStart && lockInEnd && (
          <div
            className={`rounded-xl border shadow-sm p-4 space-y-3 ${theme?.textColor}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm opacity-70">You're locked in for</span>
              <span className="text-lg font-semibold">
                {lockInPeriod} {lockInPeriod === 1 ? "month" : "months"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 text-sm border-t pt-3">
              <div>
                <div className="opacity-60 text-xs">From</div>
                <div className="font-medium">{lockInStart}</div>
              </div>
              <div className="text-right">
                <div className="opacity-60 text-xs">Until</div>
                <div className="font-medium">{lockInEnd}</div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {notes.map((note, index) => (
            <div
              key={index}
              className={`flex gap-2 rounded-xl border shadow-sm text-sm p-4 ${theme?.textColor}`}
            >
              <div>
                <Pointer className="size-4 rotate-90" />
              </div>
              <div className={`${theme?.textColor} text-wrap`}>{note}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <PrimaryButton
          text={"Continue"}
          loading={loading}
          handleClick={() => handleNext()}
          disabled={loading}
        />
      </div>
    </Div100vh>
  );
};

export default TenureScreen;
